import { t } from '@lingui/macro';
import type { EntitledEntity } from 'generated/gql';
import {
  Entitlement,
  EntityType,
  useRegisterEntitlementsLazyQuery,
  useUpdateRegisterEntitlementsMutation,
} from 'generated/gql';
import { mapEntitledEntityToEntitledEntityInput } from 'graphql/input-mappers/EntitledEntity';
import { useGlobalError } from 'hooks/useGlobalError';
import { useCallback } from 'react';

/**
 * Makes a management group the owner of a register.
 *
 * There is no OWNER role in the schema: ownership is expressed as a group entitlement on the
 * register, so the group is entitled at the highest level, `ADMINISTER` ("Beheerder").
 *
 * `updateRegisterEntitlements` replaces the complete entitlement list, so the current
 * entitlements are read first and the group is merged onto them. Without them we cannot safely
 * replace anything, and the register would lose its other entitled entities.
 *
 * Linking is best-effort: it never throws, so a failure does not block the wizard. The register
 * has already been created at that point and can still be shared through the access sidebar.
 */
export const useLinkRegisterToGroup = () => {
  const [, setGlobalError] = useGlobalError();
  const [fetchRegisterEntitlements] = useRegisterEntitlementsLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [updateRegisterEntitlements] = useUpdateRegisterEntitlementsMutation();

  return useCallback(
    async (registerId: string, groupId: string) => {
      const reportFailure = () =>
        setGlobalError(t`Het register is aangemaakt, maar kon niet aan de groep worden gekoppeld.`);

      try {
        const { data, error } = await fetchRegisterEntitlements({ variables: { registerId } });
        const entitlements = data?.registerEntitlements;
        if (error || !entitlements) {
          reportFailure();
          return;
        }

        const isGroupEntity = ({ id, type }: EntitledEntity) =>
          type === EntityType.Group && id === groupId;

        if (entitlements.find(isGroupEntity)?.entitlement === Entitlement.Administer) return;

        await updateRegisterEntitlements({
          variables: {
            registerId,
            entitledEntities: [
              // The group is dropped from the existing list and re-added as owner, so a group
              // entitled at a lower level is raised instead of ending up on the list twice.
              ...entitlements
                .filter((entity) => !isGroupEntity(entity))
                .map(mapEntitledEntityToEntitledEntityInput),
              { id: groupId, entitlement: Entitlement.Administer },
            ],
          },
          onCompleted: ({ updateRegisterEntitlements }) => {
            if (updateRegisterEntitlements.validationErrors?.length) reportFailure();
          },
          onError: reportFailure,
        });
      } catch {
        reportFailure();
      }
    },
    [fetchRegisterEntitlements, setGlobalError, updateRegisterEntitlements],
  );
};
